import { CheckCircle2, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Brand = {
  name: string;
  spec: string;
  note: string;
  expertise: string[];
};
const BRANDS: Brand[] = [{
  name: "B&D Doors",
  spec: "Australia's #1",
  note: "Original equipment cables for all B&D Panelift, Firmadoor, and Roll-A-Door models. We carry stock of every common B&D cable size.",
  expertise: ["Panelift cable kits", "Firmadoor balance cables", "B&D safety cables"]
}, {
  name: "Steel-Line",
  spec: "Premium Aus.",
  note: "Heavier-spec cables for Steel-Line sectional and roller doors. Factory-trained on Steel-Line balance systems.",
  expertise: ["Roll-A-Door Premier", "Sectional door cables", "Custom-length spec"]
}, {
  name: "Gliderol",
  spec: "Heritage brand",
  note: "Common in older Sydney homes. We hold matching cables for legacy Gliderol drums and spring setups.",
  expertise: ["Legacy drum compatibility", "Period-correct gauge", "Rust-protected build"]
}, {
  name: "Centurion",
  spec: "Mid-market",
  note: "Standard sectional door cables — we cover all Centurion Boss, Vantage, and Eco models.",
  expertise: ["Boss / Vantage cables", "Pair-matched replacement", "OEM-grade quality"]
}, {
  name: "Industrial / Commercial",
  spec: "8mm+ heavy",
  note: "Warehouse, factory, and strata doors. Heavier-gauge galvanised cables rated for high-cycle commercial use.",
  expertise: ["8mm and 10mm cables", "High-cycle rated", "Strata invoicing"]
}, {
  name: "Universal / Other",
  spec: "Any brand",
  note: "Dominator, Eco-Plus, Boss, Steel Doors, Superlift — if it's a garage door cable in Sydney, we stock it or source it within 24 hours.",
  expertise: ["Cross-brand inventory", "24hr sourcing", "Legacy door support"]
}];
export default function CableBrands() {
  return <section style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Watermark */}
      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      left: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: CREAM_DEEP,
      opacity: 0.55,
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 0
    }}>
        BRANDS
      </div>

      {/* Hairline gold rule */}
      <div aria-hidden style={{
      position: "absolute",
      top: 80,
      right: 24,
      width: 120,
      height: 1,
      background: GOLD,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 72,
        alignItems: "end"
      }} className="cable-brands-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                03 / Cables we stock
              </span>
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: INK,
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              The right cable,{" "}
              <span style={{
              color: GOLD_DARK,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                for every door
              </span>
              <br />
              made in Australia.
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: INK_MUTED,
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            We don't substitute generic cables — every replacement matches the
            original spec for length, gauge, and end fittings. Stock in the van,
            sourced same day if rare.
          </p>
        </div>

        {/* === BRAND GRID === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 0,
        border: `1px solid ${CREAM_DEEP}`,
        borderRadius: 4,
        overflow: "hidden",
        background: "#ffffff"
      }} className="cable-brands-grid">
          {BRANDS.map((b, i) => <BrandCard key={i} brand={b} index={i} />)}
        </div>

        {/* === BOTTOM STRIP === */}
        <div style={{
        marginTop: 56,
        padding: "32px 40px",
        background: "#ffffff",
        border: `1px solid ${CREAM_DEEP}`,
        borderLeft: `3px solid ${GOLD}`,
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: GOLD_DARK,
            marginBottom: 8
          }}>
              Don't know your brand?
            </div>
            <div style={{
            fontFamily: FONT_HEAD,
            fontSize: 19,
            fontWeight: 600,
            color: INK,
            lineHeight: 1.3,
            letterSpacing: "-0.01em"
          }}>
              Send a photo of your door — we'll identify the cable spec
              instantly.
            </div>
          </div>

          <a href="https://wa.me/61485000933?text=Hi%20Gold%20Garage%20Door%2C%20can%20you%20identify%20my%20garage%20door%20cable%20type%3F" target="_blank" rel="noopener noreferrer" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 22px",
          background: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontSize: 14,
          fontWeight: 700,
          borderRadius: 8,
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "background 0.2s, transform 0.2s"
        }} onMouseEnter={e => {
          e.currentTarget.style.background = NAVY_LIGHT;
          e.currentTarget.style.transform = "translateY(-2px)";
        }} onMouseLeave={e => {
          e.currentTarget.style.background = NAVY;
          e.currentTarget.style.transform = "translateY(0)";
        }}>
            Send photo via WhatsApp
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 1024px) {
          .cable-brands-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 700px) {
          .cable-brands-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .cable-brands-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>;
}

// === Brand Card ===
function BrandCard({
  brand,
  index
}: {
  brand: Brand;
  index: number;
}) {
  const isLastCol = (index + 1) % 3 === 0;
  const isLastRow = index >= 3;
  return <div className="cable-brand-card" style={{
    position: "relative",
    padding: "36px 32px",
    background: "#ffffff",
    borderRight: isLastCol ? "none" : `1px solid ${CREAM_DEEP}`,
    borderBottom: isLastRow ? "none" : `1px solid ${CREAM_DEEP}`,
    cursor: "default",
    transition: "background 0.3s",
    minHeight: 280,
    display: "flex",
    flexDirection: "column"
  }} onMouseEnter={e => {
    e.currentTarget.style.background = CREAM;
  }} onMouseLeave={e => {
    e.currentTarget.style.background = "#ffffff";
  }}>
      {/* Number / spec marker */}
      <div style={{
      fontFamily: FONT_HEAD,
      fontSize: 12,
      fontWeight: 800,
      color: GOLD_DARK,
      letterSpacing: "0.15em",
      marginBottom: 16
    }}>
        {String(index + 1).padStart(2, "0")} / {brand.spec}
      </div>

      {/* Brand name */}
      <h3 style={{
      fontFamily: FONT_HEAD,
      fontSize: 24,
      fontWeight: 800,
      color: INK,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
      margin: "0 0 14px"
    }}>
        {brand.name}
      </h3>

      {/* Note */}
      <p style={{
      fontFamily: FONT_BODY,
      fontSize: 14,
      color: INK_MUTED,
      lineHeight: 1.55,
      margin: "0 0 24px",
      flex: 1
    }}>
        {brand.note}
      </p>

      {/* Expertise list */}
      <ul style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8
    }}>
        {brand.expertise.map((item, i) => <li key={i} style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: FONT_BODY,
        fontSize: 13,
        color: INK,
        fontWeight: 500
      }}>
            <CheckCircle2 size={14} color={GOLD_DARK} strokeWidth={2.5} style={{
          flexShrink: 0
        }} />
            {item}
          </li>)}
      </ul>
    </div>;
}